import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { QueryKey as TeamManagementQueryKey } from '../../../../../../team-management/types/team-management-types';
import { QueryKey } from '../../../../../types/growth-query-key';
import { useOneOnOneMeeting } from '../../../hooks/use-one-on-one-meeting';
import { UserDTO } from '../../../lib/entities/user';

interface EditMeetingArgs {
	id: string;
	schedule_date?: string;
	schedule_duration?: number;
	schedule_time_of_day?: string;
	schedule_time_zone?: string;
}

interface UseRescheduleModalFormProps {
	onClose: VoidFunction;
	meetingId: string;
	guest: UserDTO;
}

interface SubmitRescheduleData {
	meetingDate: Date;
	startTime: Date | null;
	endTime: Date | null;
}

export const useRescheduleModalForm = ({ onClose, meetingId, guest }: UseRescheduleModalFormProps) => {
	const { t } = useTranslation('one_on_ones');
	const http = useHttpClient('v2');
	const toast = useToast();
	const queryClient = useQueryClient();
	const user = useCurrentUser();
	const { data: meeting } = useOneOnOneMeeting(meetingId, false);

	const update = useMutation(async (data: EditMeetingArgs) => {
		try {
			await http.patch(`one_on_ones/meetings/${meeting?.id ?? ''}`, data);

			queryClient.invalidateQueries([QueryKey.Meetings, params]);
			queryClient.invalidateQueries([QueryKey.Meeting]);
			queryClient.invalidateQueries([QueryKey.Meeting, meeting?.id ?? '']);
			queryClient.invalidateQueries([QueryKey.TeamActivityDetails, user.id]);
			queryClient.invalidateQueries([TeamManagementQueryKey.DirectReportsOverview]);

			onClose();
			toast({
				status: 'success',
				title: t('meeting.reschedule_modal.form.success'),
			});
			queryClient.invalidateQueries({ queryKey: [QueryKey.MeetingTimes, { eventId: meeting?.event?.id ?? '' }] });
			queryClient.invalidateQueries({ queryKey: [QueryKey.MeetingTimes, { userId: guest.id }] });
		} catch {
			onClose();
			toast({
				status: 'error',
				title: t('meeting.reschedule_modal.form.error'),
			});
		}
	});

	if (!meeting)
		return {
			isSubmitting: false,
			submitReschedule: () => {},
			availableRangeDays: 7,
		};

	const availableRangeDays = meeting.event?.frequency === 'monthly' ? 28 : meeting.event?.frequency === 'biweekly' ? 14 : 7;
	const params = { guest_id: guest.id };

	if (guest.id === user.manager_id) {
		params.guest_id = user.id;
	}

	const submitReschedule = (data: SubmitRescheduleData) => {
		if (!data.startTime || !data.endTime) {
			return;
		}

		// extract schedule components
		const hours = data.startTime.getHours().toString().padStart(2, '0');
		const minutes = data.startTime.getMinutes().toString().padStart(2, '0');
		const seconds = data.startTime.getSeconds().toString().padStart(2, '0');
		const schedule_time_of_day = `${hours}:${minutes}:${seconds}`;

		// duration in seconds
		const schedule_duration = Math.floor((data.endTime.getTime() - data.startTime.getTime()) / 1000);

		// meeting date as YYYY-MM-DD
		const year = data.meetingDate.getFullYear();
		const month = (data.meetingDate.getMonth() + 1).toString().padStart(2, '0');
		const day = data.meetingDate.getDate().toString().padStart(2, '0');
		const schedule_date = `${year}-${month}-${day}`;

		update.mutate({
			id: meeting.id,
			schedule_date,
			schedule_duration,
			schedule_time_of_day,
			schedule_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	};

	return {
		isSubmitting: update.isLoading,
		submitReschedule,
		availableRangeDays,
	};
};
